/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { ErrorHandlerService, hd } from '@nx-fe/services';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export class User {
  constructor(public firstname: string, public lastname: string, public selected: boolean = false) {}
}

@Component({
  selector: 'ssp-search-and-pick-v2',
  templateUrl: './search-and-pick.component.html',
  styleUrls: ['./search-and-pick.component.scss'],
})
export class SearchAndPickV2Component<T> implements OnInit, OnChanges, OnDestroy {
  searchForm: FormGroup;
  inputControl = new FormControl();
  @ViewChild(MatInput, { static: false }) textInput: MatInput;
  @ViewChild(MatAutocomplete, { static: false }) auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger;

  @Input() itemTemplate: TemplateRef<T>;
  @Input() customFilter: (items: T[]) => T[] = undefined;

  @Input('placeholder') placeholder = '';
  @Input('minimumFilterLength') minimumFilterLength: number;
  @Input('searchResource') searchResource: hd.IResourceClass<T>;
  @Input('selectedItem') selectedItem: T;
  @Input('queryParameters') queryParameters: any;
  @Input('excludeList') excludeList: any[];
  @Input('excludeSearchParameter') excludeSearchParameter: any;
  @Input('idField') idField = 'ticketId';
  @Input('showSearchAndCancelIcons') showSearchAndCancelIcons = false;
  @Input('disabled') disabled = false;
  @Output('onPick') onPick = new EventEmitter<T>();
  @Output('onBlur') onBlur = new EventEmitter<string>();
  @Output('onPanelToggle') onPanelToggle = new EventEmitter<boolean>();

  @Output('hasSearched') hasSearched = new EventEmitter<boolean>();

  public isOpen = (isOpen: boolean) => {
    this.onPanelToggle.emit(isOpen);
    if (!isOpen) {
      this.searchSubject.next([]);
      this.setFocus();
    }
  };

  filteredItems: Observable<T[]>;
  lastFilter = '';
  public searchFilter = '';
  isSearchIconDisplay = true;
  public iconSwitch = true;
  searchSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private elRef: ElementRef
  ) {
    this.filteredItems = this.searchSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  ngOnChanges() {
    if (this.searchForm) {
      if (this.disabled) {
        this.clearSearch();
        if (this.searchForm.enabled) {
          this.searchForm.disable();
        }
      } else {
        if (!this.searchForm.enabled) {
          this.searchForm.enable();
        }
      }
    }
  }

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      inputControl: null,
    });

    this.searchSubscription = this.searchForm
      .get('inputControl')
      .valueChanges.pipe(debounceTime(500))
      .subscribe(value => {
        this.filterChanged(value);
      });
  }

  public onBlurInput = () => {
    this.iconSwitch = true;
    this.onBlur.emit(this.searchForm.get('inputControl').value);
  };

  private searchSubject = new Subject<T[]>();
  public filterChanged = (filter: string): Observable<T[]> => {
    const searchSubject = this.searchSubject;
    if (!filter) {
      this.searchSubject.next([]);
    }
    if (this.minimumFilterLength === undefined) {
      // default if not specified in markup
      this.minimumFilterLength = 1;
    }

    if (this.minimumFilterLength === 0 || (filter && filter.length >= this.minimumFilterLength)) {
      // Add the IDs of the things to exclude from the search results
      const excludeIds = this.getExcludeIds();
      const searchParams = { searchText: filter };

      if (this.queryParameters) {
        for (const key in this.queryParameters) {
          if (this.queryParameters.hasOwnProperty(key)) {
            searchParams[key] = this.queryParameters[key];
          }
        }
      }

      if (this.excludeSearchParameter === undefined) {
        // default if not specified in markup
        this.excludeSearchParameter = 'excludeIds';
      }

      if (excludeIds) {
        searchParams[this.excludeSearchParameter] = excludeIds.toString();
      } else {
        searchParams[this.excludeSearchParameter] = null;
      }

      this.searchResource.query(
        searchParams,
        (searchResults: any[], responseHeaders?: any) => {
          const warningMessage: string = responseHeaders ? responseHeaders('sa-warning-message') : null;
          if (warningMessage) {
            const warning: hd.IError = {
              error_description: warningMessage,
            };
            this.errorHandlerService.setError(warning);
          }

          if (this.customFilter !== undefined) {
            searchResults = this.customFilter(searchResults);
          }

          searchSubject.next(searchResults);
          this.hasSearched.emit(true);
          if (filter !== this.lastFilter) {
            this.lastFilter = filter;
          }
        },
        (error: any) => {
          throw new Error('search failed: ' + error);
        }
      );
    }
    return this.filteredItems;
  };

  /** Searches the exclude list and returns a list of item IDs to exclude */
  public getExcludeIds = (): (number | string)[] => {
    let excludeIds;
    const excludeList = this.excludeList;
    if (excludeList && excludeList.length > 0) {
      excludeIds = [];
      excludeList.forEach(excludeItem => {
        if (!!excludeItem) {
          if (typeof excludeItem === 'string') {
            excludeIds.push(excludeItem);
          } else if (typeof excludeItem === 'number') {
            excludeIds.push(excludeItem);
          } else if (excludeItem[this.idField]) {
            excludeIds.push(excludeItem[this.idField]);
          }
        }
      }, this);
    }
    return excludeIds;
  };

  public getGlyphicon(selection: any) {
    let result = '';
    switch (selection.status) {
      case 'open':
        result = 'hd-icon hd-icon-open text-status-open status-icon-open';
        break;
      case 'pending':
        result = 'hd-icon hd-icon-more status-icon-pending';
        break;
      case 'resolved':
        result = 'hd-icon hd-icon-accept status-icon-resolved';
        break;
    }

    return result;
  }

  displayWrapped = (value: any) => (!!value ? this.displayFn(value.item, undefined) : '');

  displayFn = (value: T[] | T, fieldName: string): string => {
    if (this.searchInputText.value === '') {
      return;
    }
    if (fieldName === undefined) {
      fieldName = this.idField;
    }
    const display = (item: T) => {
      if (!item) {
        return '';
      }
      if (item[fieldName]) {
        return item[fieldName];
      }

      return item.toString();
    };

    let displayValue: string;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (index === 0) {
          displayValue = display(item);
        } else {
          displayValue += ', ' + display(item);
        }
      });
    } else {
      displayValue = display(value);
    }

    return displayValue;
  };

  optionClicked(event: Event, selection: T) {
    this.selectItem(selection);
    this.clearSearch();
  }

  private selectItem(selection: T) {
    this.searchFilter = this.displayFn(selection, this.idField);
    this.selectedItem = selection;
    this.onPick.emit(selection);
  }

  public clearSearch = (): void => {
    if (this.searchInputText) {
      this.searchInputText.setValue('');
      this.searchSubject.next([]);
    }
  };

  public setFocus = () => {
    this.iconSwitch = false;
    const searchInputElement = this.elRef.nativeElement.querySelector('.searchInput');
    if (searchInputElement) {
      this.removeSearchIcon();
      setTimeout(() => {
        searchInputElement.focus();
      });
    }
  };

  public removeSearchIcon() {
    this.iconSwitch = false;
    if (this.showSearchAndCancelIcons) {
      this.iconSwitch = false;
      this.isSearchIconDisplay = false;
    }
  }

  public get searchInputText() {
    return this.searchForm ? this.searchForm.get('inputControl') : null;
  }
}
